
import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import ProtectedRoute from "./components/ProtectedRoute";
import LandingPage from "./pages/LandingPage";
import NotFound from "./pages/NotFound";
import { Loader2 } from "lucide-react";
import AccountSettingsPage from './pages/AccountSettingsPage';
import SupportPage from '@/pages/SupportPage';
import { SupportChatProvider } from './contexts/SupportChatContext';

// Improved lazy-loading with better chunk naming
const Index = lazy(() => import(/* webpackChunkName: "Index" */ "./pages/Index"));
const TitanEngine = lazy(() => import(/* webpackChunkName: "TitanEngine" */ "./pages/TitanEngine"));
const LeagueDetails = lazy(() => import(/* webpackChunkName: "LeagueDetails" */ "./pages/LeagueDetails"));
const MatchDetails = lazy(() => import(/* webpackChunkName: "MatchDetails" */ "./pages/MatchDetails"));
const Prices = lazy(() => import(/* webpackChunkName: "Prices" */ "./pages/Prices"));
const AboutUs = lazy(() => import(/* webpackChunkName: "AboutUs" */ "./pages/AboutUs"));
const Dashboard = lazy(() => import(/* webpackChunkName: "Dashboard" */ "./pages/Dashboard"));
const DailyPredictions = lazy(() => import(/* webpackChunkName: "DailyPredictions" */ "./pages/DailyPredictions"));
const Leaderboard = lazy(() => import(/* webpackChunkName: "Leaderboard" */ "./pages/Leaderboard"));

// Create a custom QueryClient with improved caching
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1, // Reduce retry attempts
      gcTime: 1000 * 60 * 30, // 30 minutes cache (formerly cacheTime)
    },
  },
});

// Optimized loading fallback with reduced CLS
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-screen">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

// Fixed RouteTracker component to monitor route changes safely
const RouteTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Log transition performance without using performance marks/measures that might fail
    const navigationStart = performance.now();
    
    // Record route change time safely
    window.requestAnimationFrame(() => {
      const navigationTime = performance.now() - navigationStart;
      console.log(`Route transition time: ${navigationTime}ms`);
    });
    
    // Prefetch data for commonly accessed routes from current page
    if (location.pathname === '/dashboard') {
      // Prefetch daily predictions data
      queryClient.prefetchQuery({
        queryKey: ['daily-predictions', new Date().toISOString().split('T')[0]],
        queryFn: () => import('./pages/DailyPredictions').then(() => null),
      });
    }
    
    return () => {
      console.log(`Navigation from ${location.pathname} took ${performance.now() - navigationStart}ms`);
    };
  }, [location.pathname]);
  
  return null;
};

const App = () => {
  // Prefetch critical pages
  useEffect(() => {
    // Prefetch the dashboard component for quick access after login
    const prefetchDashboard = () => {
      const prefetchLink = document.createElement('link');
      prefetchLink.href = '/dashboard';
      prefetchLink.rel = 'prefetch';
      document.head.appendChild(prefetchLink);
      
      // Also import the component code
      import('./pages/Dashboard');
    };
    
    // Delay prefetching to prioritize initial render
    const timer = setTimeout(prefetchDashboard, 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <RouteTracker />
        <LanguageProvider>
          <AuthProvider>
            <SupportChatProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <Suspense fallback={<LoadingFallback />}>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/prices" element={<Prices />} />
                    <Route path="/about-us" element={<AboutUs />} />
                    
                    {/* Protected routes */}
                    <Route path="/dashboard" element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    } />
                    <Route path="/daily-predictions" element={
                      <ProtectedRoute>
                        <DailyPredictions />
                      </ProtectedRoute>
                    } />
                    <Route path="/matches/daily-predictions" element={
                      <ProtectedRoute>
                        <DailyPredictions />
                      </ProtectedRoute>
                    } />
                    <Route path="/tickets" element={
                      <ProtectedRoute>
                        <Index />
                      </ProtectedRoute>
                    } />
                    <Route path="/titan-engine" element={
                      <ProtectedRoute>
                        <TitanEngine />
                      </ProtectedRoute>
                    } />
                    <Route path="/titan-engine/league/:id" element={
                      <ProtectedRoute>
                        <LeagueDetails />
                      </ProtectedRoute>
                    } />
                    <Route path="/titan-engine/league/:leagueId/match/:matchId" element={
                      <ProtectedRoute>
                        <MatchDetails />
                      </ProtectedRoute>
                    } />
                    
                    {/* Leaderboard route */}
                    <Route path="/leaderboard" element={
                      <ProtectedRoute>
                        <Leaderboard />
                      </ProtectedRoute>
                    } />
                    
                    {/* Account settings page */}
                    <Route path="/settings" element={<AccountSettingsPage />} />
                    
                    {/* Support page */}
                    <Route path="/support" element={<SupportPage />} />
                    
                    {/* 404 page */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </TooltipProvider>
            </SupportChatProvider>
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
};

export default App;
